import { BaseModal, BaseModalProps } from './BaseModal';
import { ChangeEvent, useEffect, useState } from 'react';
import { Box, Checkbox } from '@mui/material';
import { theme } from '../../configs/theme';
import { weekDays } from '../../pages/settings/utils/constants/weekDays';
import { defaultStateScheduleDays } from '../../pages/settings/utils/constants/defaultStates';
import { iHandleChange } from '../../pages/settings/utils/types/IHandleChange';
import { useModal } from '../../contexts/ModalContext';
import PageForm from '../common/PageForm';
import { InputsLayout } from '../layouts/InputsLayout';
import { setDefaultStateScheduleCheckboxes } from '../../utils/setDefaultStateScheduleCheckboxes';
import { EmploymentScheduleDay } from '../../types/backendResponses/EmploymentScheduleDay';
import { useUpdateEmploymentSchedule } from '../../api/EmploymentScheduleService/useUpdateEmploymentSchedule';
import { useDeleteEmploymentSchedule } from '../../api/EmploymentScheduleService/useDeleteEmploymentSchedule';
import { RadioButtonGroup } from '../common/RadioButtonGroup';
import { EmploymentScheduleType } from '../../types/EmploymentScheduleType';
import { CreateEmploymentScheduleRequest } from '../../types/backendRequests/CreateEmploymentScheduleRequest';
import useAPI from '../../hooks/useAPI';
import { buildApiUrl } from '../../api/config';
import { TextField } from '../common/TextField';
import { handleZodValidation, ValidationError } from '../../utils/handleZodValidation';
import { employmentSchedulePageSchema } from '../../schemas/employmentSchedulePageSchema';
import { TextFieldClock } from '../common/TextFieldClock';
import { EmploymentSchedule } from '../../types/backendResponses/EmploymentSchedule';

export default function EmploymentScheduleModal({ name }: BaseModalProps) {
  const { action: updateEmploymentSchedule, isLoading: isUpdating } = useUpdateEmploymentSchedule();
  const { action: deleteEmploymentSchedule, isLoading: isDeleting } = useDeleteEmploymentSchedule();

  const { modalState, setModalUpdated, open, close } = useModal();
  const [errors, setErrors] = useState<ValidationError<typeof employmentSchedulePageSchema>>({});

  const { callAPI: createSchedule } = useAPI({ method: 'POST' });

  const [scheduleDays, setScheduleDays] =
    useState<(EmploymentScheduleDay | null)[]>(defaultStateScheduleDays);

  const [isChecked, setIsChecked] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);

  const [request, setRequest] = useState<CreateEmploymentScheduleRequest>({
    name: '',
    hoursPerWeek: 0,
    scheduleId: '',
    employmentScheduleType: EmploymentScheduleType.UntilFurtherNotice,
    employmentSchedule: [],
  });

  const mode = modalState?.EmploymentScheduleModal?.payload?.mode;
  // The row from the schedule list, which is already the whole schedule.
  const schedule: EmploymentSchedule | undefined =
    modalState?.EmploymentScheduleModal?.payload?.schedule;

  useEffect(() => {
    if (schedule && schedule.employmentSchedule) {
      setRequest({
        name: schedule.name,
        employmentSchedule: schedule.employmentSchedule,
        scheduleId: schedule.id,
        employmentScheduleType: schedule.employmentScheduleType,
      });
      setIsChecked(setDefaultStateScheduleCheckboxes(weekDays, schedule));
      setScheduleDays(schedule.employmentSchedule);
    }
  }, [schedule]);

  const handleSubmit = async () => {
    handleZodValidation({
      data: { ...request },
      schema: employmentSchedulePageSchema,
      onError: setErrors,
      onSuccess: async () => {
        if (mode === 'edit') {
          await updateSchedule();
        } else if (mode === 'create') {
          await handleCreateSchedule();
        }
      },
    });
  };

  const updateSchedule = async () => {
    setModalUpdated(false);

    const requestWithId = {
      ...request,
      id: schedule?.id,
    };

    const { scheduleId, ...rest } = requestWithId;

    const success = await updateEmploymentSchedule(rest);

    if (success) {
      setModalUpdated(true);
      close('EmploymentScheduleModal');
    }
  };

  const handleCreateSchedule = async () => {
    setModalUpdated(false);

    if (!request) {
      return;
    }
    try {
      await createSchedule(buildApiUrl('employmentSchedule'), request);
      setModalUpdated(true);
      close('EmploymentScheduleModal');
    } catch (e) {}
  };

  const handleDelete = async () => {
    if (!schedule) {
      return;
    }
    setModalUpdated(false);
    const success = await deleteEmploymentSchedule(schedule.id);
    if (success) {
      setModalUpdated(true);
      close('EmploymentScheduleModal');
    }
  };

  const handleOpenConfirmModal = () => {
    open('ConfirmModal', {
      title: 'Bekräfta',
      content: 'Är du säker på att du vill ta bort detta anställningsschema?',
      confirmText: 'Ja',
      cancelText: 'Nej',
      onConfirm: async () => {
        await handleDelete();
      },
    });
  };

  const handleChange = ({ event, index, inputName, date }: iHandleChange) => {
    let checkboxIsChecked = isChecked;
    if (event?.target?.value !== undefined && inputName !== 'hoursWorked') {
      checkboxIsChecked[index] = event?.target?.checked;
      setIsChecked(checkboxIsChecked);
    }

    let days = [...scheduleDays];

    days[index] = {
      day:
        event?.target?.value !== undefined && inputName !== 'hoursWorked'
          ? event.target.value
          : scheduleDays[index]?.day,
      startTime:
        date !== undefined && inputName === 'startTime' ? date : (days[index]?.startTime ?? ''),
      endTime: date !== undefined && inputName === 'endTime' ? date : (days[index]?.endTime ?? ''),
      workedHours:
        event?.target?.value !== undefined && inputName === 'hoursWorked'
          ? event.target.value
          : (scheduleDays[index]?.workedHours ?? ''),
    };

    if (checkboxIsChecked[index] === false) {
      days[index] = {
        day: '',
        endTime: '',
        startTime: '',
        workedHours: '',
      };
    }

    setScheduleDays(days);
    // @ts-ignore
    setRequest((prev) => ({ ...prev, employmentSchedule: days }));
  };

  const handleChangeChosenEmploymentType = (e: ChangeEvent<HTMLInputElement>) => {
    setRequest((prev) => ({
      ...prev,
      employmentScheduleType: e.target.value as EmploymentScheduleType,
    }));
  };

  return (
    <BaseModal
      title={
        mode === 'edit'
          ? `Redigera anställningsschema ${schedule?.name}`
          : 'Skapa anställningsschema'
      }
      name="EmploymentScheduleModal"
    >
      <PageForm
        buttonText={mode === 'create' ? `Skapa` : 'Uppdatera'}
        buttonIsDisabled={isUpdating || isDeleting}
        buttonOnClick={() => handleSubmit()}
        onDelete={mode === 'edit' ? handleOpenConfirmModal : undefined}
      >
        <RadioButtonGroup
          groupValue={request.employmentScheduleType}
          name="ScheduleType"
          required
          wrapperProps={{
            label: 'Schematyp',
          }}
          onChange={(e) => handleChangeChosenEmploymentType(e)}
          radioButtons={[
            { title: 'Tillsvidare', value: EmploymentScheduleType.UntilFurtherNotice },
            { title: 'Timanställning', value: EmploymentScheduleType.Hourly },
          ]}
        />
        <InputsLayout>
          <TextField
            name="scheduleName"
            error={!!errors.name}
            errorText={errors.name}
            value={request.name}
            onChange={(e) =>
              setRequest((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            required
            wrapperProps={{ label: 'Schemanamn' }}
          />
        </InputsLayout>
        {weekDays.map((day, index) => {
          return (
            <Box
              key={day.value}
              sx={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}
              padding={'8px 0'}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Checkbox
                  value={day.value}
                  name="scheduledDays"
                  checked={isChecked[index]}
                  onChange={(e) =>
                    handleChange({
                      event: e,
                      index: index,
                    })
                  }
                  sx={{
                    padding: '0',
                    '&.Mui-checked': {
                      color: theme.palette.customColors.emerald,
                    },
                  }}
                />
                <Box>{day.name}</Box>
              </Box>

              {isChecked[index] && (
                <Box>
                  <InputsLayout>
                    <TextFieldClock
                      required
                      wrapperProps={{
                        label: 'Starttid',
                      }}
                      value={scheduleDays[index]?.startTime ?? ''}
                      name="startTime"
                      // @ts-ignore
                      error={!!errors?.[`employmentSchedule-${index}-startTime`]}
                      // @ts-ignore
                      errorText={errors?.[`employmentSchedule-${index}-startTime`]}
                      onChange={(e) =>
                        handleChange({
                          index: index,
                          date: e.target.value,
                          inputName: 'startTime',
                        })
                      }
                    />
                    <TextFieldClock
                      required
                      // @ts-ignore
                      error={!!errors?.[`employmentSchedule-${index}-endTime`]}
                      // @ts-ignore
                      errorText={errors?.[`employmentSchedule-${index}-endTime`]}
                      wrapperProps={{
                        label: 'Sluttid',
                      }}
                      value={scheduleDays[index]?.endTime ?? ''}
                      name="endTime"
                      onChange={(e) =>
                        handleChange({ index: index, date: e.target.value, inputName: 'endTime' })
                      }
                    />
                  </InputsLayout>
                  {request.employmentScheduleType === EmploymentScheduleType.UntilFurtherNotice && (
                    <InputsLayout>
                      <TextFieldClock
                        required
                        // @ts-ignore
                        error={!!errors?.[`employmentSchedule-${index}-workedHours`]}
                        // @ts-ignore
                        errorText={errors?.[`employmentSchedule-${index}-workedHours`]}
                        name="hoursWorked"
                        value={request?.employmentSchedule![index].workedHours || ''}
                        onChange={(e) =>
                          handleChange({ index: index, inputName: 'hoursWorked', event: e })
                        }
                        wrapperProps={{
                          label: 'Dagens antal arbetstimmar',
                        }}
                      />
                    </InputsLayout>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </PageForm>
    </BaseModal>
  );
}
